import useAgentInterception from '../hooks/agentInterception/useAgentInterception.hooks';

import {
  InfoTransmissionMessage,
  messagingActions,
  AgentInterceptionType,
} from '../reducers/messagingSlice';
import { useAppDispatch } from '../app/hooks';
import useSendMessageHooks from '../hooks/useSendMessage/useSendMessage.hooks';
import { printCurrency } from '../utils/money';
import { useRef } from 'react';

import { formatCurrency } from '../utils/TTSMessage';
import { POS_CART_ITEM_PRICE_INTENT, TOTAL_TTS } from '../utils/constants';
import { TOTAL_PLACEHOLDER } from '../constants';

interface OrderItem {
  name: string;
  pos_id: string;
  quantity: number;
  price: string;
  options?: OrderItem[];
}

export const usePOSCart = function () {
  const dispatch = useAppDispatch();
  const { sendMessage } = useSendMessageHooks();
  const { sendAgentInterception } = useAgentInterception();
  const eventStartTime = useRef<number>(0);
  const sendAgentInterventionEvent = () => {
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.TTS_SENT,
      },
    });
  };

  const getPrice = (price: string): string => {
    const numericPrice = parseFloat(price);
    // Even if quantity is 2, the price includes the combined quantity --- so not doing any multiplication
    const totalPrice = numericPrice * 1;
    // Convert to integer cents for printCurrency
    const priceInCents = Math.round(totalPrice * 100);
    return printCurrency(priceInCents, 'USD');
  };

  const sendMetric = (message: string) => {
    const payload: Partial<InfoTransmissionMessage> = {
      data: { type: 'METRIC', message: message },
    };
    dispatch(messagingActions.sendInfo(payload as any));
  };

  const setTimestamp = function () {
    eventStartTime.current = new Date().getTime();
  };

  const onItemClick = function (item: OrderItem) {
    const timePressed = Math.floor(
      (new Date().getTime() - eventStartTime.current) / 100
    );
    eventStartTime.current = 0;
    const total = formatCurrency(calculateItemTotalPrice(item));
    sendAgentInterventionEvent();
    sendMetric(POS_CART_ITEM_PRICE_INTENT + ' intent clicked');
    sendMessage(TOTAL_TTS.replace(TOTAL_PLACEHOLDER, total), {
      intent: POS_CART_ITEM_PRICE_INTENT,
      timeDelta: timePressed,
    });
  };

  const calculateItemTotalPrice = function (item: OrderItem): number {
    let total = parseInt((Number(item.price) * 100).toFixed(0));
    if (item.options?.length) {
      item.options.forEach((option) => {
        total += calculateItemTotalPrice(option);
      });
    }
    return total;
  };

  const onOrderTotalClick = function (intent: string, price: number) {
    const timePressed = Math.floor(
      (new Date().getTime() - eventStartTime.current) / 100
    );
    eventStartTime.current = 0;
    const amount = parseInt(Number(price).toFixed(0));
    const total = formatCurrency(amount);
    sendAgentInterventionEvent();
    sendMetric(intent + ' intent clicked');
    sendMessage(TOTAL_TTS.replace(TOTAL_PLACEHOLDER, total), {
      intent: intent,
      timeDelta: timePressed,
    });
  };

  return { onItemClick, getPrice, setTimestamp, onOrderTotalClick };
};
